import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { SxProps, Theme } from "@mui/material";
import { Checkbox, ListItemText, MenuItem, TextField, Typography } from "@mui/material";
import type { ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { selectMeasureSubgroups } from "../../../../../store/selectors/metaDataSelectors";
import type { SubgroupMeasureOption } from "../utilities/measureSubgroups";
import { getSelectAllState, toggleAllSubgroupMeasures } from "../utilities/measureSubgroups";

const SELECT_ALL_VALUE = "select-all";

const selectAllItemSx: SxProps<Theme> = (theme) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
  py: 0.5,
  borderBottom: 1,
  borderColor: "divider",
  backgroundColor: theme.palette.background.paper,
  backgroundImage: "var(--Paper-overlay, none)",
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover}), var(--Paper-overlay, none)`,
  },
});

interface Props {
  options: SubgroupMeasureOption[];
  selectedMeasureIds: string[];
  disabled?: boolean;
  onChange: (measureIds: string[]) => void;
}

const SubgroupMeasuresSelect = ({ options, selectedMeasureIds, disabled, onChange }: Props) => {
  const measureSubgroups = useSelector(selectMeasureSubgroups);

  const subgroupNameById = new Map(measureSubgroups.map((subgroup) => [subgroup.id, subgroup.name]));
  const getCurrentSubgroupName = (subgroupId: string | undefined): string =>
    (subgroupId && subgroupNameById.get(subgroupId)) || "No Group";
  const selectAllState = getSelectAllState(options, selectedMeasureIds);
  const hasSelectableOptions = options.some((option) => !option.isLocked);
  const selectedCaptions = options
    .filter((option) => selectedMeasureIds.includes(option.measureId))
    .map((option) => option.caption)
    .join(", ");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value: unknown = event.target.value;
    if (!Array.isArray(value)) {
      return;
    }
    if (value.includes(SELECT_ALL_VALUE)) {
      onChange(toggleAllSubgroupMeasures(options, selectedMeasureIds));
      return;
    }
    onChange(value.filter((measureId): measureId is string => typeof measureId === "string"));
  };

  return (
    <TextField
      select
      fullWidth
      label="Custom Measures"
      value={selectedMeasureIds}
      disabled={disabled}
      slotProps={{
        select: {
          multiple: true,
          renderValue: () => selectedCaptions,
          MenuProps: { slotProps: { paper: { sx: { width: 0, maxHeight: 400 } }, list: { sx: { pt: 0 } } } },
        },
      }}
      onChange={handleChange}
    >
      {hasSelectableOptions && (
        <MenuItem value={SELECT_ALL_VALUE} sx={selectAllItemSx}>
          <Checkbox checked={selectAllState === "all"} indeterminate={selectAllState === "some"} sx={{ py: 0.75 }} />
          <ListItemText
            primary={
              <>
                Select all{" "}
                <Typography component="span" color="textSecondary">
                  ({options.length})
                </Typography>
              </>
            }
          />
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option.measureId} value={option.measureId} disabled={option.isLocked}>
          <Checkbox checked={selectedMeasureIds.includes(option.measureId)} />
          <ListItemText
            primary={option.caption}
            secondary={getCurrentSubgroupName(option.subgroupId)}
            slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
          />
          {option.isLocked && <LockOutlinedIcon color="secondary" sx={{ ml: 1 }} />}
        </MenuItem>
      ))}
      {options.length === 0 && (
        <MenuItem disabled>
          <Typography>No custom measures yet</Typography>
        </MenuItem>
      )}
    </TextField>
  );
};

export default SubgroupMeasuresSelect;
